import React, { useCallback, useState } from 'react';
import { Button, Modal, ModalProps } from 'react-bootstrap';
import { Auth, CognitoUser } from '@aws-amplify/auth';

import { Loader } from '@/components';
import { publicService } from '@/lib/services';
import { PlatformModel } from '@/lib/models';
import { constructUrl, getEncryptedTokens, setHackettAccessTokenHeaders } from '@/lib/utils';

interface PlatformSelectionModalProps extends ModalProps {
	user: CognitoUser;
}

export const PlatformSelectionModal = ({ user, ...props }: PlatformSelectionModalProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [platforms, setPlatforms] = useState<PlatformModel[]>([]);

	const redirectUserToUrl = useCallback(
		async (url: string) => {
			try {
				const encryptedTokens = await getEncryptedTokens(user);
				const urlObj = new URL(url);
				const searchParams = setHackettAccessTokenHeaders({
					defaultUrl: urlObj.search,
					token: encryptedTokens,
				});

				const targetUrl = constructUrl(`${urlObj.origin}${urlObj.pathname}`, searchParams);
				window.location.href = urlObj.hash ? `${targetUrl}${urlObj.hash}` : targetUrl;
			} catch (_error) {
				window.location.href = url;
			}
		},
		[user]
	);

	const handleOnEnter = useCallback(async () => {
		try {
			setIsLoading(true);

			const userAttributes = await Auth.userAttributes(user);
			const platformAttributeValue = userAttributes.find((cognitoUserAttribute) => {
				return cognitoUserAttribute.Name === 'custom:platform';
			})?.Value;
			const parsedPlatformAttributeValue: string[] = platformAttributeValue
				? JSON.parse(platformAttributeValue)
				: [];
			const response = await publicService.getPlatforms({ platform: parsedPlatformAttributeValue }).fetch();

			if (response.platforms.length === 1) {
				redirectUserToUrl(response.platforms[0].signOnUrl);
			}

			setPlatforms(response.platforms);
			setIsLoading(false);
		} catch (error) {
			window.location.href = '/';
		}
	}, [redirectUserToUrl, user]);

	return (
		<Modal centered onEnter={handleOnEnter} {...props}>
			<Modal.Header>
				<Modal.Title>Select Platform</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{isLoading ? (
					<Loader />
				) : (
					<>
						{platforms.map((platform) => {
							return (
								<Button
									key={platform.platformId}
									className="d-block w-100 mb-2"
									onClick={() => redirectUserToUrl(platform.signOnUrl)}
								>
									{platform.platformName}
								</Button>
							);
						})}
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<div>
					<Button variant="link" onClick={props.onHide}>
						Close
					</Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
};
